import { useContext } from 'react'

import TripContext from 'tripPlanner/contexts/TripContext'
import type { FullTrip } from 'tripPlanner/types/common'
import type { PublicTripItem } from 'tripPlanner/types/publicTrip'
import type {
  AttractionItem,
  EventItem,
  ExperienceItem,
  NoteItem,
  RestaurantBarItem,
  ThingToDoItem,
  TripItem,
} from 'tripPlanner/types/tripItem'
import { sortBy } from 'lib/array/arrayUtils'

export const itemTypesWithNoEndDate = [
  'ATTRACTION',
  'RESTAURANT_BAR',
] satisfies Array<
  Exclude<TripItem, { endDate: moment.Moment | undefined }>['type']
>

export function itemIsInItinerary(
  item: TripItem | PublicTripItem,
  trip: { startDate?: moment.Moment, endDate?: moment.Moment } | undefined,
): boolean {
  return (
    !!item.startDate &&
    !!trip?.startDate &&
    !!trip.endDate &&
    item.startDate.isBetween(trip.startDate, trip.endDate, 'day', '[]')
  )
}

export function isSingleDateSelectionItem(
  item: TripItem,
): item is
| AttractionItem
| EventItem
| RestaurantBarItem
| ThingToDoItem
| NoteItem
| ExperienceItem {
  return (
    item.type === 'ATTRACTION' ||
    item.type === 'EVENT' ||
    item.type === 'RESTAURANT_BAR' ||
    item.type === 'THING_TO_DO' ||
    item.type === 'NOTE' ||
    item.type === 'EXPERIENCE'
  )
}

export function useItemOutsideOfTripDates(item: TripItem) {
  const tripContext = useContext(TripContext)
  if (tripContext?.startDate && tripContext?.endDate) {
    let isInValidStartDate = false
    let isInValidEndDate = false
    if (item.startDate) {
      isInValidStartDate =
        item.startDate < tripContext.startDate ||
        item.startDate > tripContext.endDate
    }
    if ('endDate' in item && item.endDate) {
      isInValidEndDate =
        item.endDate < tripContext.startDate ||
        item.endDate > tripContext.endDate
    }
    return isInValidStartDate || isInValidEndDate
  }
  return false
}

export function checkIfTripItemHasOverlappingTimes(trip: FullTrip, item: TripItem): {
  isOverlapping: boolean
  previousItem: TripItem | undefined
  nextItem: TripItem | undefined
} {
  if (!item.startDate && !item.startTime) {
    return {
      isOverlapping: false,
      previousItem: undefined,
      nextItem: undefined,
    }
  }
  if (item.startTime) {
    const sameDay = trip.items.filter(
      tripItem =>
        tripItem.startDate &&
        item.startDate &&
        tripItem.startDate.isSame(item.startDate, 'day'),
    )
    const sortedItems = sortBy(sameDay, item => item.itineraryRankString, 'asc')

    const currentIndex = sortedItems.findIndex(tripItem => tripItem.id === item.id)

    const previousItem = currentIndex > 0 ? sortedItems[currentIndex - 1] : undefined
    const nextItem = currentIndex < sortedItems.length - 1 ? sortedItems[currentIndex + 1] : undefined
    if (previousItem?.type === 'ACCOMMODATION' || nextItem?.type === 'ACCOMMODATION') {
      return {
        isOverlapping: false,
        previousItem: undefined,
        nextItem: undefined,
      }
    }

    if (previousItem?.startTime && previousItem.startTime >= item.startTime) {
      return {
        isOverlapping: true,
        previousItem,
        nextItem: undefined,
      }
    }
    if (nextItem?.startTime && nextItem.startTime <= item.startTime) {
      return {
        isOverlapping: true,
        previousItem: undefined,
        nextItem,
      }
    }
  }
  return {
    isOverlapping: false,
    previousItem: undefined,
    nextItem: undefined,
  }
}
